import React, { useEffect, useState } from 'react';
import { CheckCircle, Circle } from '@phosphor-icons/react';
import SpotsService from 'services/SpotsService';
import { BulkRentalAvailability, Spot } from 'utils/constants';
import styles from './FindParking.module.css';

export type MonthlyAccount = 'individual' | 'business';

type MonthlyAccountPickerProps = {
    spot: Spot;
    value: MonthlyAccount;
    onChange: (value: MonthlyAccount) => void;
};

// One availability lookup per spot for the life of the page; tabs remount this component often.
const availabilityCache = new Map<Spot['pk'], Promise<BulkRentalAvailability | null>>();

function loadAvailability(spot: Spot): Promise<BulkRentalAvailability | null> {
    const cached = availabilityCache.get(spot.pk);
    if (cached) return cached;
    const request = SpotsService.getSpotAvailability(spot.pk)
        .then((result) => result.bulk_rental_availability || null)
        .catch(() => null);
    availabilityCache.set(spot.pk, request);
    return request;
}

/** Business accounts are offered when the spot has self-service bulk slots and tenant capacity. */
export const hasBusinessAccounts = (availability: BulkRentalAvailability | null) =>
    Boolean(availability && availability.bulk_rental_slots_max && availability.tenants_max);

const OPTIONS: { value: MonthlyAccount; title: string; text: string }[] = [
    {
        value: 'individual',
        title: 'Individual Parking',
        text: 'Reserve and manage your own space. Ideal for solo drivers or small groups.',
    },
    {
        value: 'business',
        title: 'Business Account Parking',
        text: 'An account admin manages parking for multiple drivers with centralized billing.',
    },
];

/** Individual vs Business Account choice for the Monthly tab. Renders nothing when the spot has no business accounts. */
const MonthlyAccountPicker: React.FC<MonthlyAccountPickerProps> = ({ spot, value, onChange }) => {
    const [available, setAvailable] = useState(false);

    useEffect(() => {
        let active = true;
        loadAvailability(spot).then((availability) => {
            if (active) setAvailable(hasBusinessAccounts(availability));
        });
        return () => {
            active = false;
        };
    }, [spot]);

    if (!available) return null;

    return (
        <div className={styles.accountPicker} role="radiogroup" aria-label="Monthly parking type">
            {OPTIONS.map((option) => {
                const checked = option.value === value;
                const isBusiness = option.value === 'business';
                const activeClass = isBusiness ? styles.accountOptionActiveBusiness : styles.accountOptionActive;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        className={`${styles.accountOption} ${checked ? activeClass : ''}`}
                        onClick={() => onChange(option.value)}
                    >
                        <span className={styles.accountOptionTitle}>
                            {option.title}
                            {checked ? (
                                <CheckCircle
                                    size={16}
                                    weight="fill"
                                    className={isBusiness ? styles.accountRadioOnBusiness : styles.accountRadioOn}
                                />
                            ) : (
                                <Circle size={16} className={styles.accountRadio} />
                            )}
                        </span>
                        <span className={styles.accountOptionText}>{option.text}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default MonthlyAccountPicker;
